import { Box } from '@zoopla-eng/zds/box';
import {
    getAdded,
    getChainFree,
    getNewHomes
} from '@zoopla/listing-search/filters/modal';
import { useRouter } from 'next/router';
import { useRef, useEffect, useState } from 'react';
import { styledTextStyle, styledWrapperStyle } from './QuickFilters.styles.css';
import {
    trackAddedLast7DaysDeselected,
    trackAddedLast7DaysSelected,
    trackAllListingsSelected,
    trackChainFreeDeselected,
    trackChainFreeSelected,
    trackNewHomesDeselected,
    trackNewHomesSelected
} from './QuickFilters.tracking';
import { getSection } from './utils/getSection';
import { getPathname } from './utils/getPathname';
import { QuickFilter } from './QuickFilter';

export const QuickFilters = () => {
    const {
        query,
        pathname: currentPageRouteFilePath,
        push,
        ...router
    } = useRouter();

    const updatedQuery = useRef<{
        [key: string]: string[] | string | undefined;
    }>({
        chain_free: getChainFree(query),
        added: getAdded(query),
        new_homes: getNewHomes(query, currentPageRouteFilePath)
    });

    const updatedPathName = useRef<string | undefined>();

    useEffect(() => {
        // Sync URL parameters with shallow render
        updatedQuery.current['chain_free'] = query['chain_free'];
        updatedQuery.current['added'] = query['added'];
        if (router.isReady) {
            /* asPath provides the url as seen in the browser, we must wait for isReady before using */
            updatedPathName.current = new URL(
                router.asPath,
                'https://www.zoopla.co.uk' /* URL constructor will error without a base path so use production url, this is redundant as we then use the pathname only */
            ).pathname;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query['chain_free'], query['added'], router.isReady, router.asPath]);

    const [, forceRender] = useState({});

    const isNewHomesOnly = () =>
        getSection(updatedPathName.current) === 'new-homes';
    const isChainFree = () => updatedQuery.current['chain_free'] === 'true';
    const isAddedSevenDays = () => updatedQuery.current['added'] === '7_days';

    const hasFilterChanged = () =>
        updatedPathName.current === currentPageRouteFilePath &&
        JSON.stringify(updatedQuery.current) === JSON.stringify(query);

    const toggleQuickFilter = (filter: { [key: string]: string }) => {
        updatedQuery.current = {
            ...query,
            ...updatedQuery.current,
            ...filter,
            pn: ''
        };

        if (filter['new_homes']) {
            const section =
                filter['new_homes'] === 'only' ? 'new-homes' : 'for-sale';

            if (updatedPathName.current) {
                updatedPathName.current = getPathname(
                    updatedPathName.current,
                    section
                );
            }
        }

        if (updatedQuery.current['new_homes'] !== 'exclude') {
            delete (
                updatedQuery.current as Partial<typeof updatedQuery.current>
            )['new_homes'];
        }

        Object.keys(updatedQuery.current).forEach((key) => {
            if (updatedQuery.current[key] === '') {
                delete updatedQuery.current[key];
            }
        });

        if (hasFilterChanged()) {
            return;
        }

        forceRender({});

        // 'search-path' is the name of the dynamic route parameter and so we should not apply as a genuine query param below
        const { 'search-path': invalid, ...validParams } = updatedQuery.current;

        push({
            pathname: updatedPathName.current,
            query: validParams
        });
    };

    return (
        <Box>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                }}
            >
                <div className={styledWrapperStyle}>
                    <Box
                        display="none"
                        bpS200={{
                            display: 'flex'
                        }}
                    >
                        <p className={styledTextStyle}>Quick filters</p>
                    </Box>
                    <QuickFilter
                        isActive={isNewHomesOnly() === false}
                        onClick={() => {
                            toggleQuickFilter({
                                new_homes: 'include'
                            });
                            trackAllListingsSelected();
                        }}
                    >
                        All listings
                    </QuickFilter>
                    <QuickFilter
                        isActive={isNewHomesOnly()}
                        onClick={() => {
                            toggleQuickFilter({
                                new_homes: isNewHomesOnly() ? 'include' : 'only'
                            });
                            if (isNewHomesOnly()) {
                                trackNewHomesDeselected();
                            } else {
                                trackNewHomesSelected();
                            }
                        }}
                    >
                        New homes
                    </QuickFilter>
                    <QuickFilter
                        isActive={isChainFree()}
                        onClick={() => {
                            toggleQuickFilter({
                                chain_free:
                                    query['chain_free'] === 'true' ? '' : 'true'
                            });
                            if (isChainFree()) {
                                trackChainFreeDeselected();
                            } else {
                                trackChainFreeSelected();
                            }
                        }}
                    >
                        Chain-free
                    </QuickFilter>
                    <QuickFilter
                        isActive={isAddedSevenDays()}
                        onClick={() => {
                            toggleQuickFilter({
                                added:
                                    query['added'] === '7_days' ? '' : '7_days'
                            });
                            if (isAddedSevenDays()) {
                                trackAddedLast7DaysDeselected();
                            } else {
                                trackAddedLast7DaysSelected();
                            }
                        }}
                    >
                        Added in last 7 days
                    </QuickFilter>
                </div>
            </form>
        </Box>
    );
};
